// =============================================================================
// MeuDriver — Admin CNH review button
// -----------------------------------------------------------------------------
// Botão "Ver CNH" + dialog para visualizar a foto + aprovar/rejeitar.
// =============================================================================

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Eye, Check, X, FileBadge, Loader2 } from 'lucide-react'

type CnhStatus = 'pending' | 'verified' | 'rejected' | null

interface Props {
  instructorId: string
  instructorProfileId: string // auth.uid() do instrutor — bate com path do bucket
  cnhStatus?: CnhStatus
  onUpdated?: () => void
}

const STATUS_META: Record<Exclude<CnhStatus, null>, { label: string; cls: string }> = {
  pending:  { label: 'Pendente',    cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  verified: { label: 'Verificada',  cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rejeitada',   cls: 'bg-red-100 text-red-800 border-red-200' },
}

export function AdminCnhReviewButton({
  instructorId,
  instructorProfileId,
  cnhStatus,
  onUpdated,
}: Props) {
  const [open, setOpen] = useState(false)
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function load() {
    setLoading(true)
    try {
      const { data: files } = await supabase
        .storage
        .from('cnh')
        .list(instructorProfileId, { limit: 5 })
      const file = files?.find((f) => f.name.startsWith('cnh.'))
      if (!file) {
        setSignedUrl(null)
        return
      }
      const { data: signed } = await supabase
        .storage
        .from('cnh')
        .createSignedUrl(`${instructorProfileId}/${file.name}`, 60 * 10)
      setSignedUrl(signed?.signedUrl ?? null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function decide(action: 'verified' | 'rejected') {
    setSubmitting(true)
    const { error } = await supabase
      .from('instructors')
      .update({
        cnh_status: action,
        cnh_review_note: note.trim() || null,
        cnh_reviewed_at: new Date().toISOString(),
        cnh_verified: action === 'verified',
      })
      .eq('id', instructorId)
    setSubmitting(false)
    if (error) {
      toast.error(error.message)
      return
    }
    toast.success(action === 'verified' ? 'CNH verificada.' : 'CNH rejeitada.')
    setOpen(false)
    setNote('')
    onUpdated?.()
  }

  const isImage = signedUrl ? /\.(jpg|jpeg|png|webp)(\?|$)/i.test(signedUrl) : false

  return (
    <>
      <div className="inline-flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-[11px] gap-1"
          onClick={() => setOpen(true)}
        >
          <Eye className="h-3 w-3" />
          CNH
        </Button>
        {cnhStatus && (
          <Badge variant="outline" className={`text-[9px] px-1 ${STATUS_META[cnhStatus].cls}`}>
            {STATUS_META[cnhStatus].label}
          </Badge>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileBadge className="h-5 w-5 text-brand-600" />
              Verificação de CNH
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !signedUrl ? (
              <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
                Este instrutor ainda não enviou foto da CNH.
              </div>
            ) : isImage ? (
              <img
                src={signedUrl}
                alt="CNH do instrutor"
                className="rounded-md border max-h-96 mx-auto"
              />
            ) : (
              <a
                href={signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-brand-600 hover:underline"
              >
                Abrir arquivo (PDF) em nova aba
              </a>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Observação (visível ao instrutor se rejeitar)</label>
              <Textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Ex: a foto está cortada, refaça mostrando o documento inteiro."
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Fechar
            </Button>
            <Button
              variant="outline"
              onClick={() => decide('rejected')}
              disabled={submitting || !signedUrl}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Rejeitar
            </Button>
            <Button
              onClick={() => decide('verified')}
              disabled={submitting || !signedUrl}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Aprovar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
